import { format } from 'date-fns';

type FormatArgs = {
  granularity: string;
  level: string;
  activityList: any[];
  data: any[];
  startDate: Date | null;
  endDate: Date | null;
};

export const formatInputStatsData = ({ granularity, level, activityList, data }: FormatArgs) => {
  let results: any[] = [];

  if (granularity === 'overall') {
    if (level === 'programme') {
      const programmeSet = new Set();
      const interventionSet = new Set();

      data.forEach(datum => {
        const { id, intervention, ...rest } = datum.node;
        const programmeId = intervention && intervention.programme && intervention.programme.id;
        const interventionId = intervention && intervention.id;

        if (!programmeSet.has(programmeId)) {
          programmeSet.add(programmeId);

          results.push({
            programmeId,
            programmeNames: [intervention && intervention.programme && intervention.programme.name],
            interventionFromDate:
              intervention && intervention.fromDate && format(new Date(intervention.fromDate), 'yyyy-MM-dd'),
            interventionToDate:
              intervention && intervention.toDate && format(new Date(intervention.toDate), 'yyyy-MM-dd'),
            overallBudgetedAmount: intervention && intervention.budgetedAmount,
            overallReceivedAmount: intervention && intervention.receivedAmount,
            overallSpentAmount: intervention && intervention.spentAmount,
            overallTime: intervention && intervention.overallTime,
            overallTreatmentTime: intervention && intervention.treatmentTime,
            serviceProviderId:
              intervention && intervention.serviceProviders.length && intervention.serviceProviders[0].id,
            serviceProviderName:
              intervention && intervention.serviceProviders.length && intervention.serviceProviders[0].name,
            ...rest,
          });
        } else {
          if (!interventionSet.has(interventionId)) {
            interventionSet.add(interventionId);
            const index = results.findIndex(result => result.programmeId === programmeId);

            results[index].overallBudgetedAmount = results[index].overallBudgetedAmount + intervention.budgetedAmount;
            results[index].overallReceivedAmount = results[index].overallReceivedAmount + intervention.receivedAmount;
            results[index].overallSpentAmount = results[index].overallSpentAmount + intervention.spentAmount;
            results[index].overallTime = results[index].overallTime + intervention.overallTime;
            results[index].overallTreatmentTime = results[index].overallTreatmentTime + intervention.treatmentTime;
          }
        }
      });
    }

    if (level === 'serviceProvider') {
      const serviceProviderSet = new Set();
      const interventionSet = new Set();

      data.forEach(datum => {
        const { id, intervention, ...rest } = datum.node;
        const serviceProviderId =
          intervention && intervention.serviceProviders.length && intervention.serviceProviders[0].id;
        const interventionId = intervention && intervention.id;

        if (!serviceProviderSet.has(serviceProviderId)) {
          serviceProviderSet.add(serviceProviderId);

          results.push({
            interventionFromDate:
              intervention && intervention.fromDate && format(new Date(intervention.fromDate), 'yyyy-MM-dd'),
            interventionToDate:
              intervention && intervention.toDate && format(new Date(intervention.toDate), 'yyyy-MM-dd'),
            overallBudgetedAmount: intervention && intervention.budgetedAmount,
            overallReceivedAmount: intervention && intervention.receivedAmount,
            overallSpentAmount: intervention && intervention.spentAmount,
            overallTime: intervention && intervention.overallTime,
            overallTreatmentTime: intervention && intervention.treatmentTime,
            programmeNames: [intervention && intervention.programme && intervention.programme.name],
            serviceProviderId,
            serviceProviderName:
              intervention && intervention.serviceProviders.length && intervention.serviceProviders[0].name,
            ...rest,
          });
        } else {
          if (!interventionSet.has(interventionId)) {
            interventionSet.add(interventionId);
            const index = results.findIndex(result => result.serviceProviderId === serviceProviderId);

            results[index].overallBudgetedAmount = results[index].overallBudgetedAmount + intervention.budgetedAmount;
            results[index].overallReceivedAmount = results[index].overallReceivedAmount + intervention.receivedAmount;
            results[index].overallSpentAmount = results[index].overallSpentAmount + intervention.spentAmount;
            results[index].overallTime = results[index].overallTime + intervention.overallTime;
            results[index].overallTreatmentTime = results[index].overallTreatmentTime + intervention.treatmentTime;
            const name = intervention && intervention.programme && intervention.programme.name;
            !results[index].programmeNames.includes(name) && results[index].programmeNames.push(name);
          }
        }
      });
    }

    if (level === 'intervention') {
      const interventionSet = new Set();

      data.forEach(datum => {
        const { intervention } = datum.node;
        const interventionId = intervention && intervention.id;

        if (!interventionSet.has(interventionId)) {
          interventionSet.add(interventionId);

          results.push({
            interventionId,
            interventionName: intervention && intervention.name,
            interventionFromDate:
              intervention && intervention.fromDate && format(new Date(intervention.fromDate), 'yyyy-MM-dd'),
            interventionToDate:
              intervention && intervention.toDate && format(new Date(intervention.toDate), 'yyyy-MM-dd'),
            overallBudgetedAmount: intervention && intervention.budgetedAmount,
            overallReceivedAmount: intervention && intervention.receivedAmount,
            overallSpentAmount: intervention && intervention.spentAmount,
            overallTime: intervention && intervention.overallTime,
            overallTreatmentTime: intervention && intervention.treatmentTime,
            serviceProviderId:
              intervention && intervention.serviceProviders.length && intervention.serviceProviders[0].id,
            serviceProviderName:
              intervention && intervention.serviceProviders.length && intervention.serviceProviders[0].name,
            programmeNames: intervention && intervention.programme && intervention.programme.name,
            subjectName:
              intervention && intervention.subjects && intervention.subjects.map((value: any) => value.name).join('; '),
            focusSubjectNames: intervention && intervention.focusSubjectNames,
            gradeValues: intervention && intervention.gradeValues,
            class: intervention && intervention.class,
          });
        }
      });
    }
  }

  if (granularity === 'detailed') {
    if (level === 'activity') {
      const allowedActivities = activityList.map(value => value.id);

      data.forEach(datum => {
        const { id, intervention, ...rest } = datum.node;
        const hasAllowedActivity =
          intervention &&
          intervention.activities &&
          intervention.activities.filter((activity: any) => allowedActivities.includes(activity.id));

        if (hasAllowedActivity.length) {
          results.push({
            activityName: hasAllowedActivity[0].name,
            interventionFromDate:
              intervention && intervention.fromDate && format(new Date(intervention.fromDate), 'yyyy-MM-dd'),
            interventionToDate:
              intervention && intervention.toDate && format(new Date(intervention.toDate), 'yyyy-MM-dd'),
            serviceProviderId:
              intervention && intervention.serviceProviders.length && intervention.serviceProviders[0].id,
            serviceProviderName:
              intervention && intervention.serviceProviders.length && intervention.serviceProviders[0].name,
            ...rest,
          });
        }
      });
    } else {
      data.forEach(datum => {
        const { id, intervention, ...rest } = datum.node;

        results.push({
          programmeId: intervention && intervention.programme && intervention.programme.id,
          programmeNames: [intervention && intervention.programme && intervention.programme.name],
          interventionFromDate:
            intervention && intervention.fromDate && format(new Date(intervention.fromDate), 'yyyy-MM-dd'),
          interventionToDate:
            intervention && intervention.toDate && format(new Date(intervention.toDate), 'yyyy-MM-dd'),
          serviceProviderId:
            intervention && intervention.serviceProviders.length && intervention.serviceProviders[0].id,
          serviceProviderName:
            intervention && intervention.serviceProviders.length && intervention.serviceProviders[0].name,
          intervention,
          ...rest,
        });
      });
    }
  }

  return results;
};
